import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';

import { DISH_TAGS, KITCHEN_FLAGS, hasTag, toggleTag } from '../menuEditorPayloads';

export const labelsFormFrom = (dish) => ({
  tags: dish.tags || [],
  promotionTags: dish.promotionTags || [],
  printCategories: dish.printCategory ? dish.printCategory.split(';') : [],
  cookingCategory: dish.cookingCategory || null,
  ...Object.fromEntries(KITCHEN_FLAGS.map((flag) => [flag.key, !!dish[flag.key]])),
});

/** The dish fields the classic editor spread across "Visibility and options" and "Settings". */
export const dishWithLabels = (dish, form) => ({
  ...dish,
  tags: form.tags,
  promotionTags: form.promotionTags,
  printCategory: form.printCategories.length > 0 ? form.printCategories.join(';') : null,
  cookingCategory: form.cookingCategory,
  ...Object.fromEntries(KITCHEN_FLAGS.map((flag) => [flag.key, form[flag.key]])),
});

const Heading = ({ title, description }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    )}
  </Box>
);

Heading.propTypes = { title: PropTypes.string.isRequired, description: PropTypes.string };

const DishLabelsForm = ({ form, onChange, menuPromotionTags, printCategoryNames }) => (
  <Stack spacing={4}>
    <Box>
      <Heading title="Dietary & menu tags" description="Shown on the dish for customers." />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, columnGap: 1 }}>
        {DISH_TAGS.map((tag) => (
          <FormControlLabel
            key={tag.value}
            label={tag.label}
            title={tag.value}
            control={
              <Checkbox
                size="small"
                checked={hasTag(form.tags, tag.value)}
                onChange={(event) => onChange({ ...form, tags: toggleTag(form.tags, tag.value, event.target.checked) })}
              />
            }
          />
        ))}
      </Box>
    </Box>

    <Box>
      <Heading title="Promotion tags" description="Type a new tag and press Enter, or pick one used elsewhere on this menu." />
      <Autocomplete
        multiple
        freeSolo
        options={menuPromotionTags}
        value={form.promotionTags}
        onChange={(event, value) => onChange({ ...form, promotionTags: [...new Set(value.map((tag) => tag.trim()).filter(Boolean))] })}
        renderTags={(value, getTagProps) =>
          value.map((tag, index) => <Chip size="small" label={tag} {...getTagProps({ index })} key={tag} />)
        }
        renderInput={(params) => <TextField {...params} placeholder="Add a promotion tag" />}
      />
    </Box>

    <Box>
      <Heading title="Kitchen & printing" />
      {printCategoryNames.length > 0 ? (
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={printCategoryNames}
          value={form.printCategories}
          onChange={(event, value) => onChange({ ...form, printCategories: value })}
          renderInput={(params) => <TextField {...params} label="Print categories" helperText="Where this dish prints in the kitchen" />}
          sx={{ mb: 2 }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This menu has no cooking categories, so there is nothing to print to.
        </Typography>
      )}
      {form.cookingCategory && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2">
            KDS cooking category: <strong>{form.cookingCategory}</strong>
          </Typography>
          <Button size="small" color="inherit" onClick={() => onChange({ ...form, cookingCategory: null })}>
            Remove
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {KITCHEN_FLAGS.map((flag) => (
          <FormControlLabel
            key={flag.key}
            label={flag.label}
            control={<Switch checked={form[flag.key]} onChange={(event) => onChange({ ...form, [flag.key]: event.target.checked })} />}
          />
        ))}
      </Box>
    </Box>
  </Stack>
);

DishLabelsForm.propTypes = {
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  menuPromotionTags: PropTypes.array.isRequired,
  printCategoryNames: PropTypes.array.isRequired,
};

export default DishLabelsForm;
